import {
  Box,
  Button,
  ButtonProps,
  Icon,
  SystemStyleObject,
  Text,
} from '@chakra-ui/react'
import React, { forwardRef, useMemo, useState } from 'react'

import {
  MangoButtonStyle,
  MangoButtonVariant,
} from './styles/MangoButton.style'
import {
  mergeMultiStyleConfig,
  type StyleThemingProps,
} from './styles/styleHelpers'
import { Size } from './types'

export type SVGComponent =
  | React.FunctionComponent<React.SVGAttributes<SVGElement>>
  | React.ForwardRefExoticComponent<
      Omit<React.SVGProps<SVGSVGElement>, 'ref'> &
        React.RefAttributes<SVGSVGElement>
    >

type MangoButtonStyleProps = {
  variant?: MangoButtonVariant
  size?:
    Size.md | Size.sm | { base?: Size.md | Size.sm; md?: Size.md | Size.sm }
}

export const useMangoButtonStyleConfig = (props?: MangoButtonStyleProps) => {
  const variant = props?.variant ?? MangoButtonVariant.Default_300
  const size = props?.size ?? Size.md

  return useMemo(() => {
    return mergeMultiStyleConfig(MangoButtonStyle, {
      variant,
      size,
    } as StyleThemingProps)
  }, [variant, size])
}

export { MangoButtonVariant }

export interface MangoButtonProps extends Omit<
  ButtonProps,
  'children' | 'size' | 'onClick' | 'variant' | 'leftIcon' | 'rightIcon'
> {
  disabled?: boolean
  isLoading?: boolean
  leftIcon?: SVGComponent
  leftIconProps?: SystemStyleObject | Record<string, any>
  rightIcon?: SVGComponent
  rightIconProps?: SystemStyleObject | Record<string, any>
  leftNode?: React.ReactNode
  rightNode?: React.ReactNode
  size?:
    Size.md | Size.sm | { base?: Size.md | Size.sm; md?: Size.md | Size.sm }
  variant?: MangoButtonVariant
  children?: string | React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  onHoverStateChange?: (isHovered: boolean) => void
  iconStrokeWidth?: string
}

export const MangoButton = forwardRef<HTMLButtonElement, MangoButtonProps>(
  (
    {
      disabled = false,
      leftIcon,
      leftIconProps,
      rightIcon,
      rightIconProps,
      leftNode,
      rightNode,
      size = Size.md,
      variant = MangoButtonVariant.Default_300,
      children,
      onClick,
      onHoverStateChange,
      iconStrokeWidth = '2px',
      ...rest
    }: MangoButtonProps,
    ref,
  ) => {
    const [_, setHoverState] = useState(false)
    const handleMouseEnter = () => {
      setHoverState(true)
      onHoverStateChange?.(true)
    }
    const handleMouseLeave = () => {
      setHoverState(false)
      onHoverStateChange?.(false)
    }

    const styles = useMangoButtonStyleConfig({
      variant,
      size,
    })

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return
      onClick?.(event)
    }

    const getLeftContent = () => {
      if (!leftIcon && !leftNode) {
        return null
      }
      const content =
        leftNode ??
        (leftIcon ? (
          <Icon
            {...styles.icon}
            {...leftIconProps}
            css={{
              '& path': { strokeWidth: iconStrokeWidth },
            }}
          >
            {React.createElement(leftIcon)}
          </Icon>
        ) : null)
      return <Box css={styles.nodeContainer}>{content}</Box>
    }

    const getRightContent = () => {
      if (!rightIcon && !rightNode) {
        return null
      }

      const content =
        rightNode ??
        (rightIcon ? (
          <Icon
            {...styles.icon}
            {...rightIconProps}
            css={{
              '& path': { strokeWidth: iconStrokeWidth },
            }}
          >
            {React.createElement(rightIcon)}
          </Icon>
        ) : null)
      return <Box css={styles.nodeContainer}>{content}</Box>
    }

    return (
      <Button
        data-group
        {...styles.button}
        onClick={handleClick}
        aria-disabled={disabled}
        ref={ref}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {getLeftContent()}
        {children && <Text css={styles.text}>{children}</Text>}
        {getRightContent()}
      </Button>
    )
  },
)
